import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "@/lib/zod-lt";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/Combobox";
import { DriverForm } from "@/components/DriverForm";
import { TransportForm } from "@/components/TransportForm";

import { useStore } from "@/store";
import { DriverType, TransportType } from "@/types";

export const NAME_FIELD = "name";
export const TEL_FIELD = "tel";
export const EMAIL_FIELD = "email";
export const ADDRESS_FIELD = "address";
export const CODE_FIELD = "code";
export const VAT_FIELD = "vat";
export const BANK_ACCOUNT_FIELD = "bank_account";
export const BANK_CODE_FIELD = "bank_code";
export const BANK_NAME_FIELD = "bank_name";
export const CERTIFICATE_CODE_FIELD = "certificate_code";
export const FITOSANITARIAN_CODE_FIELD = "fitosanitarian_code";
export const INCOME_TYPE_CODE_FIELD = "income_type_code";
export const INVOICE_SERIES_FIELD = "invoice_series";
export const INVOICE_SIGNATORY_FIELD = "invoice_signatory";
export const INVOICE_TITLE_FIELD = "invoice_title";
export const DEFAULT_DRIVER_ID_FIELD = "default_driver_id";
export const DEFAULT_TRANSPORT_ID_FIELD = "default_transport_id";

const formSchema = z.object({
  [NAME_FIELD]: z.string(),
  [TEL_FIELD]: z.string(),
  [VAT_FIELD]: z.string(),
  [ADDRESS_FIELD]: z.string(),
  [BANK_ACCOUNT_FIELD]: z.string(),
  [BANK_CODE_FIELD]: z.string(),
  [BANK_NAME_FIELD]: z.string(),
  [CERTIFICATE_CODE_FIELD]: z.string(),
  [CODE_FIELD]: z.string(),
  [EMAIL_FIELD]: z.string(),
  [FITOSANITARIAN_CODE_FIELD]: z.string(),
  [INCOME_TYPE_CODE_FIELD]: z.string(),
  [INVOICE_SERIES_FIELD]: z.string(),
  [INVOICE_SIGNATORY_FIELD]: z.string(),
  [INVOICE_TITLE_FIELD]: z.string(),
  [DEFAULT_DRIVER_ID_FIELD]: z.number().nullable().optional(),
  [DEFAULT_TRANSPORT_ID_FIELD]: z.number().nullable().optional(),
});

export function Settings(): React.JSX.Element {
  const {
    reset,
    tenant,
    updateTenant,
    openDialog,
    user,
    drivers,
    transports,
    setDrivers,
    setTransports,
  } = useStore((s) => s);
  const [isDefaultDriverComboboxOpen, setDefaultDriverComboboxOpen] = useState<boolean>(false);
  const [isDefaultTransportComboboxOpen, setDefaultTransportComboboxOpen] =
    useState<boolean>(false);
  const [isCreateDriverDialogOpen, setCreateDriverDialogOpen] = useState<boolean>(false);
  const [isCreateTransportDialogOpen, setCreateTransportDialogOpen] = useState<boolean>(false);

  const defaultValues = {
    [NAME_FIELD]: tenant?.name || "",
    [TEL_FIELD]: tenant?.tel || "",
    [EMAIL_FIELD]: tenant?.email || "",
    [ADDRESS_FIELD]: tenant?.address || "",
    [CODE_FIELD]: tenant?.code || "",
    [VAT_FIELD]: tenant?.vat || "",
    [BANK_ACCOUNT_FIELD]: tenant?.bank_account || "",
    [BANK_CODE_FIELD]: tenant?.bank_code || "",
    [BANK_NAME_FIELD]: tenant?.bank_name || "",
    [CERTIFICATE_CODE_FIELD]: tenant?.certificate_code || "",
    [FITOSANITARIAN_CODE_FIELD]: tenant?.fitosanitarian_code || "",
    [INCOME_TYPE_CODE_FIELD]: tenant?.income_type_code || "35",
    [INVOICE_SERIES_FIELD]: tenant?.invoice_series || "",
    [INVOICE_SIGNATORY_FIELD]: tenant?.invoice_signatory || "",
    [INVOICE_TITLE_FIELD]: tenant?.invoice_title || "",
    [DEFAULT_DRIVER_ID_FIELD]: tenant?.default_driver_id ?? null,
    [DEFAULT_TRANSPORT_ID_FIELD]: tenant?.default_transport_id ?? null,
  };

  const form = useForm<z.infer<typeof formSchema>>({
    mode: "all",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const handleResetClick = async () => {
    openDialog({
      title: "Atkurti duomenis iš atsarginės versijos",
      message: "Ar tikrai norite atkurti duomenis iš atsarginės versijos?",
      confirmText: "Atkurti",
      onConfirm: async () => {
        await reset();
      },
    });
  };

  const canReset =
    user &&
    typeof user === "object" &&
    "id" in user &&
    user.id === "cce5ebae-c70f-443d-8bbf-c89d5972aa24";

  useEffect(() => {
    if (!tenant) return;
    form.reset(defaultValues);
  }, [tenant]);

  const selectedDefaultDriver = drivers?.find(
    (driver) => driver.id === form.getValues(DEFAULT_DRIVER_ID_FIELD),
  );
  const selectedDefaultDriverOption = selectedDefaultDriver
    ? {
        name: `${selectedDefaultDriver.name} (${selectedDefaultDriver.license_number})`,
        id: selectedDefaultDriver.id,
      }
    : null;

  const selectedDefaultTransport = transports?.find(
    (transport) => transport.id === form.getValues(DEFAULT_TRANSPORT_ID_FIELD),
  );
  const selectedDefaultTransportOption = selectedDefaultTransport
    ? {
        name: `${selectedDefaultTransport.license_plate} - ${selectedDefaultTransport.model}`,
        id: selectedDefaultTransport.id,
      }
    : null;

  const driverOptions =
    drivers
      ?.map(({ name, license_number, id }) => ({
        name: `${name} (${license_number})`,
        id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  const transportOptions =
    transports
      ?.map(({ license_plate, model, id }) => ({
        name: `${license_plate} - ${model}`,
        id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  function handleDefaultDriverSelect(option: { id: string | number }): void {
    const id = typeof option.id === "number" ? option.id : Number(option.id);
    if (!isNaN(id)) {
      form.setValue(DEFAULT_DRIVER_ID_FIELD, id);
    }
  }

  function handleDefaultTransportSelect(option: { id: string | number }): void {
    const id = typeof option.id === "number" ? option.id : Number(option.id);
    if (!isNaN(id)) {
      form.setValue(DEFAULT_TRANSPORT_ID_FIELD, id);
    }
  }

  function handleDriverCreate(driverFormData: {
    name: string;
    license_number: string;
    id?: number;
  }) {
    if (!driverFormData.id) return;
    const newDriver =
      drivers.find((d) => d.id === driverFormData.id) ??
      ({
        id: driverFormData.id,
        name: driverFormData.name,
        license_number: driverFormData.license_number,
      } as DriverType);
    setDrivers([newDriver, ...drivers]);
    setCreateDriverDialogOpen(false);
    setDefaultDriverComboboxOpen(false);
    form.setValue(DEFAULT_DRIVER_ID_FIELD, driverFormData.id);
  }

  function handleTransportCreate(transportFormData: {
    license_plate: string;
    model: string;
    id?: number;
  }) {
    if (!transportFormData.id) return;
    const newTransport =
      transports.find((t) => t.id === transportFormData.id) ??
      ({
        id: transportFormData.id,
        license_plate: transportFormData.license_plate,
        model: transportFormData.model,
      } as TransportType);
    setTransports([newTransport, ...transports]);
    setCreateTransportDialogOpen(false);
    setDefaultTransportComboboxOpen(false);
    form.setValue(DEFAULT_TRANSPORT_ID_FIELD, transportFormData.id);
  }

  async function onSubmit(formData: z.infer<typeof formSchema>): Promise<void> {
    // Ensure null values are explicitly sent (not undefined)
    const payload: Partial<typeof formData> = {
      ...formData,
      [DEFAULT_DRIVER_ID_FIELD]: formData[DEFAULT_DRIVER_ID_FIELD] ?? null,
      [DEFAULT_TRANSPORT_ID_FIELD]: formData[DEFAULT_TRANSPORT_ID_FIELD] ?? null,
    };
    await updateTenant(payload);
  }

  return (
    <>
      <h1 className="text-2xl my-4 font-bold">Nustatymai</h1>
      <div className="my-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-8 grid-cols-3">
              <FormField
                control={form.control}
                name={NAME_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Pavadinimas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={TEL_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Tel*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={EMAIL_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">El. Paštas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={ADDRESS_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Adresas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={CODE_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Kodas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={INCOME_TYPE_CODE_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Pajamų rūšies kodas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={VAT_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">PVM Kodas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={INVOICE_SERIES_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Sąskaitos Serija*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={INVOICE_TITLE_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Sąskaitos Pavadinimas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={INVOICE_SIGNATORY_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Sąskaitos Pasirašytojas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={FITOSANITARIAN_CODE_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Fitosanitarinis Kodas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={CERTIFICATE_CODE_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Sertifikato Kodas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={BANK_NAME_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Banko Pavadinimas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={BANK_CODE_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Banko Kodas*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={BANK_ACCOUNT_FIELD}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="block">Banko Sąskaita*</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={DEFAULT_DRIVER_ID_FIELD}
                render={() => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="block">Numatytasis vairuotojas</FormLabel>
                    <Combobox
                      options={driverOptions}
                      isOpen={isDefaultDriverComboboxOpen}
                      onComboboxOpenChange={setDefaultDriverComboboxOpen}
                      selectedOption={selectedDefaultDriverOption}
                      onSelect={handleDefaultDriverSelect}
                      selectLabel="Pasirinkite vairuotoją"
                      searchLabel="Ieškoti vairuotojo..."
                      notFoundLabel="Vairuotojas nerastas."
                      dialogTitle="Naujas vairuotojas"
                      dialogDescription="Sukurkite naują vairuotoją."
                      isDialogOpen={isCreateDriverDialogOpen}
                      onDialogOpenChange={setCreateDriverDialogOpen}
                    >
                      <DriverForm onDriverFormSubmit={handleDriverCreate} />
                    </Combobox>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={DEFAULT_TRANSPORT_ID_FIELD}
                render={() => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="block">Numatytasis transportas</FormLabel>
                    <Combobox
                      options={transportOptions}
                      isOpen={isDefaultTransportComboboxOpen}
                      onComboboxOpenChange={setDefaultTransportComboboxOpen}
                      selectedOption={selectedDefaultTransportOption}
                      onSelect={handleDefaultTransportSelect}
                      selectLabel="Pasirinkite transportą"
                      searchLabel="Ieškoti transporto..."
                      notFoundLabel="Transportas nerastas."
                      dialogTitle="Naujas transportas"
                      dialogDescription="Sukurkite naują transportą."
                      isDialogOpen={isCreateTransportDialogOpen}
                      onDialogOpenChange={setCreateTransportDialogOpen}
                    >
                      <TransportForm onTransportFormSubmit={handleTransportCreate} />
                    </Combobox>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex">
              <Button className="mr-4" type="submit">
                Išsaugoti
              </Button>
            </div>
          </form>
        </Form>
        {canReset ? (
          <div className="my-6">
            <p className="my-2">Atkurti duomenis iš atsarginės versijos</p>
            <Button variant="destructive" type="button" onClick={handleResetClick}>
              Atkurti
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}
